(() => {
  if (window.__ellagenixSiteOverlaysLoaded) return;
  window.__ellagenixSiteOverlaysLoaded = true;

class OverlayPopin extends HTMLElement {
  connectedCallback() {
    this.closeButton = this.querySelector('[data-overlay-close]');
    this.closeButton?.addEventListener('click', this.hide.bind(this));

    if (Shopify.designMode) {
      this.sectionElement = this.closest('.shopify-section');
      this.onSectionSelect = () => this.show();
      this.onSectionDeselect = () => this.hide(true);
      this.sectionElement?.addEventListener('shopify:section:select', this.onSectionSelect);
      this.sectionElement?.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
  }

  disconnectedCallback() {
    if (!Shopify.designMode || !this.sectionElement) return;
    this.sectionElement.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.sectionElement.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  show() {
    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
    });
  }

  hide(immediate = false) {
    this.classList.remove('is-visible');

    if (immediate) {
      this.hidden = true;
      return;
    }

    window.setTimeout(() => {
      if (!this.classList.contains('is-visible')) {
        this.hidden = true;
      }
    }, 300);
  }
}

class NewsletterPopup extends OverlayPopin {
  static STORAGE_SUBSCRIBED = 'ellagenix:newsletter-popup-subscribed';
  static STORAGE_APPEARED = 'ellagenix:newsletter-popup-appeared';

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.subscribed === 'true') {
      this.markSubscribed();
      this.show();
      return;
    }

    if (this.shouldAutoOpen()) {
      const delay = Number.parseInt(this.dataset.delay || '0', 10) * 1000;
      this.autoOpenTimeout = window.setTimeout(() => this.show(), delay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.autoOpenTimeout);
  }

  shouldAutoOpen() {
    if (sessionStorage.getItem(CheckoutExitSurvey.STORAGE_SUPPRESS_NEWSLETTER) === 'true') {
      return false;
    }

    if (localStorage.getItem(NewsletterPopup.STORAGE_SUBSCRIBED) === 'true') {
      return false;
    }

    if (
      this.dataset.showOnce === 'true' &&
      localStorage.getItem(NewsletterPopup.STORAGE_APPEARED) === 'true'
    ) {
      return false;
    }

    return true;
  }

  markSubscribed() {
    localStorage.setItem(NewsletterPopup.STORAGE_SUBSCRIBED, 'true');
  }

  show() {
    if (this.dataset.showOnce === 'true') {
      localStorage.setItem(NewsletterPopup.STORAGE_APPEARED, 'true');
    }

    super.show();
  }
}

class PrivacyBanner extends OverlayPopin {
  connectedCallback() {
    super.connectedCallback();

    this.acceptButton = this.querySelector('[data-privacy-accept]');
    this.declineButton = this.querySelector('[data-privacy-decline]');

    this.acceptButton?.addEventListener('click', this.onAccept.bind(this));
    this.declineButton?.addEventListener('click', this.onDecline.bind(this));

    this.loadConsentApi();
  }

  loadConsentApi() {
    if (!window.Shopify?.loadFeatures) {
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) return;
        this.onConsentApiReady();
      },
    );
  }

  onConsentApiReady() {
    if (Shopify.designMode) return;

    if (window.Shopify.customerPrivacy?.shouldShowBanner()) {
      this.show();
    }
  }

  onAccept() {
    window.Shopify.customerPrivacy?.setTrackingConsent(true, () => this.hide());
  }

  onDecline() {
    window.Shopify.customerPrivacy?.setTrackingConsent(false, () => this.hide());
  }
}

class CheckoutExitSurvey extends OverlayPopin {
  static STORAGE_LAST_SHOWN = 'ellagenix:checkout-survey-last-shown';
  static STORAGE_CHECKOUT_PENDING = 'ellagenix:checkout-pending-survey';
  static STORAGE_CHECKOUT_VISITED = 'ellagenix:checkout-visited';
  static STORAGE_SUPPRESS_NEWSLETTER = 'ellagenix:suppress-newsletter';
  static CHECK_EVENT = 'ellagenix:checkout-return-check';
  static PENDING_MAX_AGE_MS = 30 * 60 * 1000;

  connectedCallback() {
    super.connectedCallback();

    this.form = this.querySelector('.checkout-exit-survey__form');
    this.bodyInput = this.querySelector('[data-survey-body]');
    this.reasonInputs = this.querySelectorAll('[data-survey-reason]');
    this.otherField = this.querySelector('[data-survey-other]');
    this.otherInput = this.querySelector('[data-survey-other-input]');
    this.commentInput = this.querySelector('[data-survey-comment]');
    this.steps = this.querySelectorAll('[data-survey-step]');
    this.nextButton = this.querySelector('[data-survey-next]');
    this.backButton = this.querySelector('[data-survey-back]');

    this.onCheckoutReturnCheck = this.tryAutoOpen.bind(this);

    this.nextButton?.addEventListener('click', this.onNext.bind(this));
    this.backButton?.addEventListener('click', this.onBack.bind(this));
    this.reasonInputs?.forEach((input) => {
      input.addEventListener('change', this.onReasonChange.bind(this));
    });
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    document.addEventListener(CheckoutExitSurvey.CHECK_EVENT, this.onCheckoutReturnCheck);

    if (this.dataset.submitted === 'true') {
      this.markShown();
      this.show();
      return;
    }

    this.tryAutoOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CheckoutExitSurvey.CHECK_EVENT, this.onCheckoutReturnCheck);
    window.clearTimeout(this.autoOpenTimeout);
  }

  tryAutoOpen() {
    if (this.dataset.submitted === 'true') return;
    if (this.autoOpenTimeout) return;
    if (this.classList.contains('is-visible')) return;
    if (!this.shouldAutoOpen()) return;

    const delay = Number.parseInt(this.dataset.delay || '0', 10) * 1000;
    this.autoOpenTimeout = window.setTimeout(() => {
      this.autoOpenTimeout = null;
      if (this.shouldAutoOpen()) {
        this.show();
      }
    }, delay);
  }

  getPendingCheckoutTimestamp() {
    try {
      const pending = localStorage.getItem(CheckoutExitSurvey.STORAGE_CHECKOUT_PENDING);
      if (!pending) return null;

      const timestamp = Number.parseInt(pending, 10);
      if (!timestamp || Date.now() - timestamp > CheckoutExitSurvey.PENDING_MAX_AGE_MS) {
        localStorage.removeItem(CheckoutExitSurvey.STORAGE_CHECKOUT_PENDING);
        return null;
      }

      return timestamp;
    } catch (error) {
      return null;
    }
  }

  hasCheckoutReturnSignal() {
    if (this.getPendingCheckoutTimestamp()) return true;

    if (sessionStorage.getItem(CheckoutExitSurvey.STORAGE_CHECKOUT_VISITED)) return true;

    const referrer = document.referrer || '';
    if (/checkout|checkouts/i.test(referrer)) return true;

    const navigationEntry = performance.getEntriesByType('navigation')[0];
    if (navigationEntry?.type === 'back_forward' && this.getPendingCheckoutTimestamp()) {
      return true;
    }

    return false;
  }

  isTestMode() {
    return new URLSearchParams(window.location.search).has('checkout_survey_test');
  }

  shouldAutoOpen() {
    if (this.isTestMode()) return true;
    if (Shopify.designMode) return false;
    if (!this.hasCheckoutReturnSignal()) return false;

    const lastShown = Number.parseInt(
      localStorage.getItem(CheckoutExitSurvey.STORAGE_LAST_SHOWN) || '0',
      10,
    );
    const cooldownMs = Number.parseInt(this.dataset.cooldown || '3600', 10) * 1000;

    if (lastShown && Date.now() - lastShown < cooldownMs) {
      return false;
    }

    return true;
  }

  markShown() {
    if (this.isTestMode()) return;

    localStorage.setItem(CheckoutExitSurvey.STORAGE_LAST_SHOWN, String(Date.now()));
    localStorage.removeItem(CheckoutExitSurvey.STORAGE_CHECKOUT_PENDING);
    sessionStorage.removeItem(CheckoutExitSurvey.STORAGE_CHECKOUT_VISITED);
    sessionStorage.setItem(CheckoutExitSurvey.STORAGE_SUPPRESS_NEWSLETTER, 'true');
  }

  prepareForDisplay() {
    document.querySelector('cart-drawer')?.close?.();
    document.body.classList.remove('overflow-hidden');
  }

  show() {
    if (!Shopify.designMode && !this.isTestMode()) {
      if (
        !localStorage.getItem(CheckoutExitSurvey.STORAGE_CHECKOUT_PENDING) &&
        !sessionStorage.getItem(CheckoutExitSurvey.STORAGE_CHECKOUT_VISITED) &&
        this.hasCheckoutReturnSignal()
      ) {
        const timestamp = String(Date.now());
        localStorage.setItem(CheckoutExitSurvey.STORAGE_CHECKOUT_PENDING, timestamp);
        sessionStorage.setItem(CheckoutExitSurvey.STORAGE_CHECKOUT_VISITED, timestamp);
      }

      this.markShown();
    }

    this.prepareForDisplay();
    super.show();
  }

  setStep(stepNumber) {
    this.steps?.forEach((step) => {
      const isActive = Number.parseInt(step.dataset.surveyStep || '0', 10) === stepNumber;
      step.hidden = !isActive;
    });
  }

  getSelectedReasonLabel() {
    const selected = this.querySelector('[data-survey-reason]:checked');
    if (!selected) return '';

    if (selected.value === 'other') {
      return this.otherInput?.value.trim() || selected.nextElementSibling?.textContent?.trim() || '';
    }

    return selected.nextElementSibling?.textContent?.trim() || selected.value;
  }

  onReasonChange(event) {
    const isOther = event.target.value === 'other';
    if (!this.otherField) return;

    this.otherField.hidden = !isOther;
    if (this.otherInput) {
      this.otherInput.required = isOther;
      if (!isOther) {
        this.otherInput.value = '';
      }
    }
  }

  onNext() {
    const selected = this.querySelector('[data-survey-reason]:checked');
    if (!selected) {
      this.reasonInputs?.[0]?.focus();
      return;
    }

    if (selected.value === 'other' && !this.otherInput?.value.trim()) {
      this.otherInput?.focus();
      return;
    }

    this.setStep(2);
    this.commentInput?.focus();
  }

  onBack() {
    this.setStep(1);
    this.querySelector('[data-survey-reason]:checked')?.focus();
  }

  buildContactBody() {
    const reason = this.getSelectedReasonLabel();
    const comment = this.commentInput?.value.trim() || '—';

    return [
      'Checkout terk anketi',
      `Sebep: ${reason}`,
      `Ek yorum: ${comment}`,
      `Sayfa: ${window.location.pathname}`,
      `Zaman: ${new Date().toISOString()}`,
    ].join('\n');
  }

  onSubmit(event) {
    if (!this.bodyInput) return;

    const selected = this.querySelector('[data-survey-reason]:checked');
    if (!selected) {
      event.preventDefault();
      this.setStep(1);
      this.reasonInputs?.[0]?.focus();
      return;
    }

    if (selected.value === 'other' && !this.otherInput?.value.trim()) {
      event.preventDefault();
      this.setStep(1);
      this.otherInput?.focus();
      return;
    }

    this.bodyInput.value = this.buildContactBody();
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}

if (!customElements.get('privacy-banner')) {
  customElements.define('privacy-banner', PrivacyBanner);
}

if (!customElements.get('checkout-exit-survey')) {
  customElements.define('checkout-exit-survey', CheckoutExitSurvey);
}
})();
